import React from 'react';
import { Card, Descriptions, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { WorkOrderBoqOperationRow } from '@/services/workOrder.service';

const { Text } = Typography;

/** Line identity fields shared across SCI / FIR / WO line joins. */
export interface InspectionWorkLineSnapshot {
  line_number?: number | null;
  item_code?: string | null;
  item_description?: string | null;
  revision?: string | null;
  drawing_reference_number?: string | null;
  part_number?: string | null;
  part_description?: string | null;
  heat_number?: string | null;
  planned_quantity?: number | null;
  uom?: string | null;
}

export interface InspectionWorkLineDetailsCardProps {
  title?: string;
  workOrderNumber?: string | null;
  /** Current stage/operation label (SCI). */
  currentOperationLabel?: string | null;
  line?: InspectionWorkLineSnapshot | null;
  /** BOQ operations for this line (Final Inspection / full work details). */
  operations?: WorkOrderBoqOperationRow[];
  operationsLoading?: boolean;
  showOperations?: boolean;
}

function dash(v: unknown): string {
  const s = v == null ? '' : String(v).trim();
  return s || '—';
}

/**
 * Read-only work identity block for QMS Inspection / Final Inspection.
 * Reuses the same field set as Dimensional Inspection / WO line identity.
 */
export const InspectionWorkLineDetailsCard: React.FC<InspectionWorkLineDetailsCardProps> = ({
  title = 'Work details',
  workOrderNumber,
  currentOperationLabel,
  line,
  operations = [],
  operationsLoading = false,
  showOperations = false,
}) => {
  const lineLabel =
    line?.line_number != null ? `Line ${line.line_number}` : line ? 'Line' : '—';

  const opColumns: ColumnsType<WorkOrderBoqOperationRow> = [
    {
      title: 'Seq',
      dataIndex: 'sequence_number',
      width: 64,
    },
    {
      title: 'Operation',
      dataIndex: 'description',
      ellipsis: true,
    },
    {
      title: 'Outsourced',
      width: 100,
      render: (_, row) =>
        row.is_outsourced ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag>,
    },
    {
      title: 'Est. hours',
      width: 100,
      render: (_, row) =>
        row.estimated_runtime_hours != null
          ? Number(row.estimated_runtime_hours).toFixed(2)
          : '—',
    },
    {
      title: 'Stages',
      width: 220,
      render: (_, row) => {
        const stages =
          Array.isArray(row.work_order_stages) && row.work_order_stages.length > 0
            ? row.work_order_stages
            : row.work_order_stage
              ? [row.work_order_stage]
              : [];
        if (stages.length === 0) return '—';
        return stages.map((s) => (
          <Tag key={s.id} style={{ marginBottom: 2 }}>
            {s.stage_code || s.stage_name || s.id.slice(0, 8)} · {s.status}
          </Tag>
        ));
      },
    },
  ];

  return (
    <Card title={title} style={{ marginBottom: 16 }}>
      <Descriptions bordered size="small" column={2}>
        <Descriptions.Item label="Work order">{dash(workOrderNumber)}</Descriptions.Item>
        <Descriptions.Item label="Line">{lineLabel}</Descriptions.Item>
        <Descriptions.Item label="Item code">{dash(line?.item_code)}</Descriptions.Item>
        <Descriptions.Item label="Revision">{dash(line?.revision)}</Descriptions.Item>
        <Descriptions.Item label="Description" span={2}>
          {dash(line?.item_description)}
        </Descriptions.Item>
        <Descriptions.Item label="Drawing ref.">
          {dash(line?.drawing_reference_number)}
        </Descriptions.Item>
        <Descriptions.Item label="Part no.">{dash(line?.part_number)}</Descriptions.Item>
        <Descriptions.Item label="Part description" span={2}>
          {dash(line?.part_description)}
        </Descriptions.Item>
        <Descriptions.Item label="Heat no.">{dash(line?.heat_number)}</Descriptions.Item>
        <Descriptions.Item label="Planned qty">
          {line?.planned_quantity != null
            ? `${line.planned_quantity}${line.uom ? ` ${line.uom}` : ''}`
            : '—'}
        </Descriptions.Item>
        {currentOperationLabel ? (
          <Descriptions.Item label="Current operation" span={2}>
            {currentOperationLabel}
          </Descriptions.Item>
        ) : null}
      </Descriptions>

      {showOperations ? (
        <div style={{ marginTop: 16 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Operations{line?.revision ? ` (Revision ${line.revision})` : ''}
          </Text>
          <Table<WorkOrderBoqOperationRow>
            size="small"
            rowKey="id"
            loading={operationsLoading}
            pagination={false}
            dataSource={operations}
            columns={opColumns}
            locale={{ emptyText: 'No operations for this line' }}
          />
        </div>
      ) : null}
    </Card>
  );
};

export default InspectionWorkLineDetailsCard;
